import { NavLink } from 'react-router-dom';
import { FiShoppingCart } from 'react-icons/fi';
import { FcLike } from 'react-icons/fc';
import { TiStarFullOutline } from 'react-icons/ti';
import { Product } from '../types/types';
import { FaRegHeart } from 'react-icons/fa';
import React, { useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Alert from './Alert';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import useAddToCart from '../hooks/useAddToCart';

interface ProductCardProps {
  product: Product;
  formatNumber: (number: number | bigint) => string;
}

export default function ProductCardProfile({
  product,
  formatNumber,
}: ProductCardProps) {
  const [showAlert, setShowAlert] = useState(false);
  const { t } = useTranslation();

  const handleAddToCart = useAddToCart(); 

  const getDiscountNumber = (discount: string): number => {
    const parsed = Number(discount);
    return isNaN(parsed) ? 0 : parsed;
  };

  return (
    <NavLink to={`/products/details/${product.id}`} key={product.id}>
      {showAlert && (
        <Alert
          message="상품이 장바구니에 추가되었습니다."
          onClose={() => setShowAlert(false)}
        />
      )}
      <div className="bg-white w-[200px] mobile:w-full shadow-md border overflow-hidden cursor-pointer">
        <div className="bg-[#ffffff] relative w-full h-full aspect-square overflow-hidden mobile:w-full mobile:relative mobile:aspect-square">
          {/* 이미지 */}
          <LazyLoadImage
            src={`${process.env.REACT_APP_API_BASE_URL}${product.image}`}
            alt={product.name}
            className="w-full h-full object-cover object-center transition-transform duration-300 ease-in-out hover:scale-110"
            effect="blur" // 블러 효과
          />
        </div>
        <div className="px-2 mobile:px-1">
          {/* 유저 */}
          <div className="flex mt-1">
            <div className="relative ">
              <div className="w-[40px] h-[40px] ml-[-6px] mobile:ml-[-1px] flex items-center justify-center">
                <img
                  src={`${process.env.REACT_APP_API_BASE_URL}${product.profileImageUrl}`}
                  alt={product.name}
                  className="w-[24px] h-[24px] mobile:w-[28px] mobile:h-[28px] object-cover transition-transform rounded-full duration-300"
                />
              </div>
              <div className="absolute bottom-[6px] right-[4px] mobile:right-[2px] mobile:bottom-[4px]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={11}
                  height={11}
                  viewBox="0 0 48 48"
                  className="mobile:w-[12px] mobile:h-[12px]"
                >
                  <g fill="none" strokeLinejoin="round" strokeWidth={3}>
                    <path
                      fill="#009dff"
                      stroke="#009dff"
                      d="M24 44C29.5228 44 34.5228 41.7614 38.1421 38.1421C41.7614 34.5228 44 29.5228 44 24C44 18.4772 41.7614 13.4772 38.1421 9.85786C34.5228 6.23858 29.5228 4 24 4C18.4772 4 13.4772 6.23858 9.85786 9.85786C6.23858 13.4772 4 18.4772 4 24C4 29.5228 6.23858 34.5228 9.85786 38.1421C13.4772 41.7614 18.4772 44 24 44Z"
                    ></path>
                    <path
                      stroke="#fff"
                      strokeLinecap="round"
                      d="M16 24L22 30L34 18"
                    ></path>
                  </g>
                </svg>
              </div>
            </div>
            <div className=" ml-[5px]  mobile:overflow-hidden">
              <p className="text-[#009dff] inline-block text-xs font-normal tracking-wider mobile:text-xs">
                크릿소속
              </p>
              <p className="text-[#1a1a1a] font-semibold tracking-wider text-xs mobile:text-xs overflow-hidden text-nowrap text-ellipsis w-[140px] mobile:w-[130px]">
                {product.nickname}
              </p>
            </div>
          </div>
          {/* 상품명 / 가격 */}
          <div className="px-1 mt-2">
            {/* 상품명 */}
            <div className="flex">
              <p className="text-[#000] font-medium text-base mobile:text-xs overflow-hidden tracking-wide max-w-[230px] text-nowrap text-ellipsis mobile:pl-1 ">
                {product.name}
              </p>
            </div>

            {/* 가격 */}
            <div className="mobile:px-1">
              <div className="h-[21px] flex items-center justify-between">
                {getDiscountNumber(product.discount) > 0 && (
                  <p className="text-[#009dff] font-medium text-sm mobile:text-sm">
                    {product.discount}%
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <p className="text-[#292b2b] text-nowrap text-sm mobile:text-sm font-semibold">
                    {t('product_card.moneyterms')} {formatNumber(product.price)}
                  </p>
                  <span className="line-through text-nowrap text-xs font-light mobile:text-sm text-[#acacac]">
                    ({t('product_card.moneyterms')}{' '}
                    {formatNumber(product.originalPrice)})
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* 별점 / 장바구니 */}
          <div className="mt-1 py-1">
            <div className="flex items-center justify-between">
              <div className="mobile:px-1 flex items-center font-normal gap-1 mobile:gap-[2px] text-xs mobile:text-[10px] text-[#878f918a] ">
                <TiStarFullOutline className="mb-[2px] mobile:mb-[0px]" />
                <span className="text-[#878f91]">{product.review}</span>
              </div>
              <div className="flex">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={14}
                  height={14}
                  viewBox="0 0 24 24"
                  onClick={(e) => {
                    e.preventDefault();
                    handleAddToCart(product, product.image);
                    setShowAlert(true);
                  }}
                  className='cursor-pointer'
                >
                  <path
                    fill="#878f918a"
                    className='hover:fill-MainColor transition-all duration-300 ease-in-out'
                    fillRule="evenodd"
                    d="M9 8a3 3 0 1 1 6 0zM7 8a5 5 0 0 1 10 0h3a1 1 0 0 1 .996 1.09l-.835 9.182A3 3 0 0 1 17.174 21H6.826a3 3 0 0 1-2.987-2.728L3.004 9.09A1 1 0 0 1 4 8z"
                    clipRule="evenodd"
                  ></path>  
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </NavLink>
  );
}
